import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useTranslation } from 'react-i18next'

import {
  addClientToEquipmentQueue as addClientToEquipmentQueueService,
  removeClientFromEquipmentQueue as removeClientFromEquipmentQueueService,
  notifyClientInEquipmentQueue as notifyClientInEquipmentQueueService,
  getEquipmentQueueClients,
} from '_services/tanning/equipment-queue.service'
import { useToast } from '@hapana/shared/web/components/shared/toast/toast.hooks'
import { TOAST_PROPS_OPTIONS } from '@hapana/shared/web/components/shared/toast/toast.constants'

export const useAddClientToEquipmentQueue = (options) => {
  return useMutation(addClientToEquipmentQueueService, { ...options })
}

export const useEquipmentQueueClients = (siteId, equipmentId) => {
  const variables = equipmentId ? { siteId, equipmentId } : { siteId }
  return useQuery([getEquipmentQueueClients.key, variables], getEquipmentQueueClients.query, {
    enabled: !!siteId,
  })
}

export const useRemoveClientFromEquipmentQueue = (options) => {
  const { t } = useTranslation('checkIn')
  const { openToast } = useToast()
  const queryClient = useQueryClient()

  const onSuccess = async (data, variables) => {
    openToast({
      title: t('checkIn:equipments.waitline.removedFromQueue'),
      severity: TOAST_PROPS_OPTIONS.SEVERITY.SUCCESS,
      horizontal: TOAST_PROPS_OPTIONS.HORIZONTAL.CENTER,
    })
    await queryClient.invalidateQueries(getEquipmentQueueClients.key)
    options?.onSuccess?.(data, variables)
  }

  const onError = (error, variables) => {
    const errorMessage = error?.response?.errors?.[0]?.message
    openToast({
      title: errorMessage || t('checkIn:equipments.waitline.removeFromQueueError'),
      severity: TOAST_PROPS_OPTIONS.SEVERITY.ERROR,
      horizontal: TOAST_PROPS_OPTIONS.HORIZONTAL.CENTER,
    })
    options?.onError?.(error, variables)
  }

  return useMutation(removeClientFromEquipmentQueueService, { ...options, onSuccess, onError })
}

export const useNotifyClientInEquipmentQueue = (options) => {
  const { t } = useTranslation('checkIn')
  const { openToast } = useToast()

  const onSuccess = async (data, variables) => {
    openToast({
      title: t('checkIn:equipments.waitline.notificationSent'),
      severity: TOAST_PROPS_OPTIONS.SEVERITY.SUCCESS,
      horizontal: TOAST_PROPS_OPTIONS.HORIZONTAL.CENTER,
    })
    options?.onSuccess?.(data, variables)
  }

  const onError = (error, variables) => {
    const gqlError = error?.response?.errors?.[0]
    const failReason = gqlError?.extensions?.details?.failReason
    const title =
      failReason === 'EQUIPMENT_NOT_AVAILABLE'
        ? t('checkIn:equipments.waitline.notificationEquipmentNotAvailable')
        : failReason === 'NOTIFICATION_SEND_FAILED'
          ? t('checkIn:equipments.waitline.notificationSendFailed')
          : t('checkIn:equipments.waitline.notificationError')
    openToast({
      title,
      severity: TOAST_PROPS_OPTIONS.SEVERITY.ERROR,
      horizontal: TOAST_PROPS_OPTIONS.HORIZONTAL.CENTER,
    })
    options?.onError?.(error, variables)
  }

  return useMutation(notifyClientInEquipmentQueueService, { ...options, onSuccess, onError })
}
